/**
 * Public Pages Copy
 * 
 * Centralized copy for all public-facing pages including home page,
 * for-artists page, and other public content.
 * 
 * This file contains exact copies of all user-facing text from the original
 * hardcoded implementations to enable centralized copy management.
 */

export const publicPagesCopy = {
  // Shared "WHY CORAL EXISTS" section copy. Used by Home (via WhyCoralSection
  // between Hero and HowItWorks), PublicArtist, and ArtistPayoutCollection
  // (both between Hero and Vision).
  whyCoralExists: {
    sectionTitle: 'WHY CORAL EXISTS',
    headline: 'Music is more than product.\nArt struggles while industry profits.',
  },

  home: {
    hero: {
        title: 'Artists you love.\nMoney that reaches them.\nCommunity that gets it.',
        subtitle: 'Back artists you choose. One monthly contribution, split your way. The community? People who care enough to back music\'s future.',
      },

      valueProposition: {
        problem: 'Music is a product. Streaming pays pennies. Artists struggle while platforms profit.',
        solution: 'Coral is a new way to back music, as art, not product. One monthly amount, split across the artists you choose. This unlocks reviews, conversations, and discovery powered by real people\'s actual taste.'
      },

      howItWorks: {
        sectionTitle: 'HOW IT WORKS',
        personaliseTitle: 'Add any artists you want. Set one monthly amount.',
        whatACoralIs: 'A coral is your collection of artists, backed with one monthly amount that is split between them and paid out directly each month. You choose who is in it and change it whenever you like.',
        commonsAndEcosystem: 'Your coral also unlocks the commons, where you can discover and discuss music with others who get it. And the more of us who build corals, the richer the ecosystem for music becomes.',
        anyArtistsTooltip: 'Can\'t find an artist? Add them anyway.<br />We\'ll notify them about waiting funds.<br /><br />Funds wait until claimed. You can refund uncollected funds anytime if you change your mind.',
      },
      
      vision: {
        sectionTitle: 'OUR VISION',
        description: 'Musicians making a living.\nScenes that thrive.\nMusic worth discovering.',
      },
      
      coralDynamics: {
        sectionTitle: 'SMALL ACTS, REAL IMPACT',
      },
      
      support: {
        sectionTitle: 'STRAIGHT ANSWERS',
        faqDescription: 'Everything you need to know about Coral.',
      },
      
      cta: {
        personalise: 'START YOUR CORAL',
        addToEcosystem: 'Join the Community',
      },
      
      altText: {
        blueWave: 'Blue Wave',
        darkBlueWave: 'Dark Blue Wave',
      },
  },
  
  forArtists: {
    hero: {
      title: 'A no-obligation way for',
      titleBreak: 'fans to back your art.',
      subtitle: 'Regular income alongside your gigs, releases, and merch.',
    },
    
    keyAdvantages: {
      sectionTitle: 'KEY ADVANTAGES',
      beyondSimple: {
        title: 'Monthly backing',
        description: 'Fans choose you as one of the artists they back each month. You don\'t need to release, tour or post anything to receive it.',
      },
      focusOnMusic: {
        title: 'Earn from gigs you didn\'t play',
        description: 'Tickets sold on Coral Tickets send part of the booking charge to an artist on Coral. Once a fan backs you, that artist can be you.',
      },
      sustainableBacking: {
        title: 'Focus on music, not content',
        description: 'We build your page from your artist name. No posting, perks or reward tiers to run. The obligation rests between all of us, not on you.',
      },
    },
    
    strategy: {
      sectionTitle: 'OUR STRATEGY',
      subtitle: 'Your community, backing the work you make.',
      whatTitle: 'WHAT',
      paragraph1: 'Gigs, downloads, merch and vinyl each sell a moment or an object. Coral backs the music itself.',
      howTitle: 'HOW',
      howDescription: 'Income reaches you in two ways. Fans build a coral: the set of artists they back, with a monthly contribution split among them. And tickets sold on Coral Tickets send part of the booking charge to an artist on Coral. Both arrive in one monthly payout.',
      whyTitle: 'WHY',
      whyDescription: 'Music belongs to all of us, and it only thrives when we look after it together. Coral makes that simple.',
    },
    
    ecosystem: {
      sectionTitle: 'CORAL TICKETS',
      heading: 'Built so music lifts music.',
      description: 'Tickets sold on Coral Tickets pay the band on the night, and part of the booking charge goes to another artist on Coral. Buyers can choose who, or we pick from eligible artists. You\'re eligible once a fan backs you and your payout details are set up.',
      linkText: 'Explore Coral Tickets',
    },
    
    coordinatedScale: {
      sectionTitle: 'COORDINATED SCALE',
      description: 'Small amounts, from enough people, add up. See how it scales.',
    },
    
    nextSteps: {
      sectionTitle: 'WHAT\'S NEXT?',
      steps: [
        'Use the tool above to create your Artist Page and assets',
        'Share them with your community via Linktree, socials, and at gigs',
        'When you receive your first contribution, we\'ll contact you via your official channels (Instagram, website, or manager) with a unique code',
        'Visit the payment collection link, provide your email, unique code, and PayPal username to sign up and start receiving monthly payments',
      ],
      termsText: 'Read our Artist Terms and Conditions for more details on how we can cooperate.',
      termsLinkText: 'Artist Terms and Conditions',
    },
    
    support: {
      sectionTitle: 'SUPPORT',
      faqTitle: 'FAQs',
      faqDescription: 'Everything you need to know about Coral.',
    },
    
    finalCta: {
      description: 'Musicians making a living.\nScenes that thrive.\nMusic worth discovering.',
    },
    
    cta: {
      createArtistPage: 'CREATE YOUR ARTIST PAGE',
      generateProfile: 'GENERATE YOUR PROFILE',
    },
    
    altText: {
      blueWave: 'Blue Wave',
      darkBlueWave: 'Dark Blue Wave',
    },
  },
  
  about: {
    hero: {
      title: 'Our dream is a vibrant, healthy, and diverse ecosystem of music, artists and communities.',
    },
    mainSection: {
      sectionLabel: 'ABOUT WEARECORAL',
      heading: 'What can we make together?',
      content: [
        'wearecoral reflects the inquiry, how can we cooperate at scale to perpetuate art?',
        'Our answer is a platform for supporting the musicians and scenes we love. Contributors\' monthly pledges are distributed among multiple artists, through a dynamic, user-configured system. This additional income improves financial stability for artists so they can focus on their craft, enriching our lives with their music.',
        'wearecoral is designed to harness our collective capacity to care for the artists and scenes we love, instilling a sense of power together, and profound agency to shape the future we dream of.',
        'wearecoral is committed to creating an organisation that prioritises collective benefit over individual gain. Through open financial practices, engaging our community in key decisions, and putting 51% of any profits we take out of the company back into the music ecosystem, we seek to reflect the principles that collective action is more powerful than individual efforts, and we put in more than we take out.',
        'With wearecoral we\'re active participants in the future being made today.',
      ],
      callToAction: 'Let\'s play together.',
    },
    contributorsSection: {
      sectionLabel: 'OUR CONTRIBUTORS',
      heading: 'Behind the scenes',
    },
    photographySection: {
      sectionLabel: 'PHOTOGRAPHY',
      heading: 'Live photos',
      intro: 'The live photos on wearecoral and Coral Tickets are by Dominique Berns, shared with the blessing of the artists in them.',
      photographer: { name: 'Dominique Berns', handle: 'dominiqueberns.jpg' },
      artists: [
        { name: 'Abbey Lane', handle: 'abbeylane_music' },
        { name: 'Alibii', handle: 'alibiiduo' },
        { name: 'Anna Dine', handle: 'annadinemusic' },
        { name: 'Horrorshow', handle: 'horrorshowcrew' },
        { name: 'Kick on Kings', handle: 'kickon.kings' },
        { name: 'Melita', handle: 'melitamusica' },
      ],
    },
  },
  
  contact: {
    hero: {
      title: 'Let\'s talk.',
    },
    mainSection: {
      sectionLabel: 'CONNECT',
      heading: 'Let\'s build this together.',
      content: [
        'wearecoral is a different type of organisation. Transparent and committed to involving our community in dialogue and decision-making.',
        'Media enquiries and partnerships are welcome.',
        'You can reach out to us via hello@wearecoral.org.',
      ],
      email: 'hello@wearecoral.org',
    },
  },
  
  principles: {
    hero: {
      title: 'What guides us.',
    },
    mainSection: {
      sectionLabel: 'PRINCIPLES',
      heading: 'What\'s Guiding Us',
      principles: [
        {
          title: 'Intentional',
          description: 'We\'ve designed wearecoral to support the mission and vision of the organisation. Ongoing alignment requires regular discussion and reflection.',
        },
        {
          title: 'Living System',
          description: 'Like our natural counterpart, wearecoral is designed to be dynamic, continuously evolving in response to signals and feedback. Like a living ecosystem, we intend to calibrate, adjust and modify the platform in response to signal and feedback to ensure we\'re fostering a vibrant, thriving music ecosystem in line with our mission.',
        },
        {
          title: 'Collective',
          description: 'We believe in community and collaboration. Our view is that we can create an impactful and enduring support system for music by acting and receiving together.',
        },
        {
          title: 'Fair',
          description: 'Facilitating communities and collective decision making is a complex process. We are committed to fairness, equity and inclusion in any decision-making.',
        },
        {
          title: 'Dialogue',
          description: 'Acting together requires us to be attuned to each other. To listen, make sense and act in concert. We\'ll make intentional spaces for those who want to be involved in the conversation.',
        },
        {
          title: 'Transparency',
          description: 'As a platform that provides tools and features that helps communities collaborate, it\'s critical that wearecoral is trusted as a worthy partner, capable of facilitating fair and enabling infrastructure for the musical ecosystem. To do so we\'ll provide transparency across our operations and member contributions.',
        },
      ],
    },
    ctaSection: {
      heading: 'A vibrant, healthy, and diverse ecosystem of music, artists and communities.',
    },
  },
  
  trust: {
    hero: {
      title: 'Trust allows us to cooperate.',
    },
    mainSection: {
      sectionLabel: 'TRUST BY DESIGN',
      heading: 'How We Operate',
      intro: 'Transparency is a necessary foundation for the development of trust between wearecoral, contributors, and artists. Below is every contribution: what\'s reached the artists you back, and what we\'re still holding for them until they collect it. The full transaction data is downloadable as a public ledger. Every row carries its transaction id, so contributors can match their own contribution and see exactly where it went, and artists can audit their payouts.',
      sections: {
        corporateStructure: {
          title: 'Corporate Structure',
          content: 'wearecoral operates as an Australian proprietary limited company (Pty Ltd). We operate multiple bank accounts that maintain a clear separation between the funds allocated for artists and the platform fees that constitute revenue for the company.',
        },
        profitPledge: {
          title: 'Where Our Profits Go',
          content: 'wearecoral is a social enterprise. Whenever we take profits out of the company, 51% goes back into the music ecosystem and no more than 49% to shareholders. By the music ecosystem, we mean the people, places and systems that keep music alive, from artists and venues to music charities and grassroots projects.',
        },
      },
    },
    financials: {
      sectionLabel: 'WHERE THE MONEY FLOWS',
      heading: 'Where the money flows.',
      intro: 'Every contribution is tracked, across the community platform and Coral Tickets. These figures come from the same public record you can download below.',
      stats: {
        collected: 'Contributed to artists',
        distributed: 'Reached artists',
        awaiting: 'Held for artists',
      },
      awaitingNote: 'Money held for artists sits in a separate, segregated account. It\'s paid out each month once the artist joins and verifies. If an artist never joins, their funds stay put, collectible whenever they do, and you can refund anything uncollected at any time.',
      errorLabel: 'Figures are unavailable right now. The full record is always in the file below.',
      csvLinkText: 'Download the full transaction data',
    },
    ctaSection: {
      heading: 'A vibrant, healthy, and diverse ecosystem of music, artists and communities.',
    },
  },

  publicArtist: {
    hero: {
      subtitle: (artistName: string) => `Back ${artistName}. Your money goes direct, not via royalties.`,
      community: 'Join a community sustaining the artists and scenes you love.',
      platformComparison: {
        text: 'More of your money reaches them than Bandcamp, Patreon, or',
        linkText: 'anywhere else in music',
        tooltipHtml: 'Coral: 82 to 88% reaches your artists · Social enterprise · 51% of any profits we take out go back into the music ecosystem<br /><br />Patreon: roughly 80 to 87% · 10% fee plus processing · For-profit, venture-backed<br /><br />Bandcamp: roughly 80% digital, 85% above $5,000 · For-profit, owned by Songtradr<br /><br />Merch: 40 to 60% to artist (manufacturing and retail costs)<br /><br />Streaming: a fraction of a cent per play',
      },
    },

    yourImpact: {
      sectionTitle: 'YOUR IMPACT',
      heading: 'Your coral does three things.',
      benefits: {
        first: 'Ensures real money monthly for artists you back, not shareholders.',
        second: 'Unlocks the Coral Commons. A space for discovery and conversation, powered by humans, not algorithms.',
        third: (artistName: string) => `Makes ${artistName} eligible for artist contributions from tickets sold on Coral Tickets.`,
      },
    },

    ecosystem: {
      sectionTitle: 'ARTIST ECOSYSTEM',
      heading: (artistName: string) => `${artistName}'s ecosystem at a glance.`,
      body: "Real signals: who's backing, how often, what stage. The shape of this artist's community right now.",
    },

    vision: {
      sectionTitle: 'OUR VISION',
      description: 'Musicians making a living.\nScenes that thrive.\nMusic worth discovering.',
    },

    coralDynamics: {
      sectionTitle: 'SMALL ACTS, REAL IMPACT',
    },

    support: {
      sectionTitle: 'STRAIGHT ANSWERS',
      faqDescription: (artistName: string) => `Everything you need to know about backing ${artistName} through Coral.`,
    },
    
    cta: {
      buildCoral: (artistName: string) => `Back ${artistName}`,
      startCoral: (artistName: string) => `Back ${artistName}`,
    },
    
    altText: {
      blueWave: 'Blue Wave',
      darkBlueWave: 'Dark Blue Wave',
    },
    
    meta: {
      title: (artistName: string) => `Back ${artistName} on Coral`,
      description: (artistName: string) => `Back ${artistName} and join a community lifting music.`,
    },

    notFound: {
      title: 'Artist Not Found',
      message: "We couldn't find the artist you're looking for.",
      cta: 'Back to home',
    },
  },

  payoutCollection: {
    hero: {
      subtitleLine1: 'Your music moves people.',
      subtitleLine2: "They're expressing it with corals.",
      collectIntro: 'Ready to collect their contributions? Complete the verification process using the secure information provided to you.',
    },

    howItWorks: {
      header: 'How It Works',
      bullets: {
        first: 'Monthly contributions from people who value your work',
        second: 'Artist releases and creative updates are automatically sent to backers (no action required)',
        third: 'Built around community connection, not transactional relationships',
      },
    },

    platformComparison: {
      prefix: '+ lower fees than ',
      linkText: 'other platforms',
      tooltipHtml: 'wearecoral: 82 to 88% reaches your artists<br />Bandcamp: roughly 80% digital, 85% above $5,000<br />Patreon: roughly 80 to 87%<br />Merchandise: 40 to 60% to artist<br />Streaming: a fraction of a cent per play',
    },

    form: {
      title: 'Verify Your Identity',
      codeLabel: 'Verification Code',
      emailLabel: 'Your Email',
      paypalLabel: 'PayPal Email',
      paypalHelp: "This is where we'll send your monthly contributions",
      termsPrefix: 'I have read and agree to the ',
      termsLinkText: 'Artist Terms and Conditions',
      validationError: 'Please agree to the Artist Terms and Conditions before proceeding.',
      missingUrlError: 'Missing verification URL. Please check your link and try again.',
      genericError: 'An error occurred during verification. Please try again.',
      submitLabel: 'Complete Verification',
      submittingLabel: 'Verifying...',
    },

    result: {
      successFollowUp: 'You will receive your first payment at the end of this month.',
    },

    notFound: {
      title: 'Artist Not Found',
      message: "We couldn't find the artist you're looking for.",
      cta: 'Back to home',
    },

    // Vision is single-sourced from publicArtist.vision.{sectionTitle, description}
    // so the three PublicArtist-subtree pages cannot drift.

    support: {
      sectionTitle: 'SUPPORT',
      faqTitle: 'FAQs',
      faqDescription: 'Everything you need to know about wearecoral.',
    },

    altText: {
      darkBlueWave: 'Blue Wave',
    },
  },

  thankyou: {
    hero: {
      title: 'Thank You for Creating Your Coral',
      subtitle: "Let's nurture a thriving and diverse ecosystem together.",
      authedSubtitle: 'Your coral has been created successfully!',
      loggedOutSubtitle: 'Now check your email for a magic link to sign in to wearecoral.',
    },

    cta: {
      returnToDashboard: 'Return to Dashboard',
    },

    // Vision is single-sourced from publicArtist.vision.{sectionTitle, description}
    // so the three PublicArtist-subtree pages cannot drift.

    altText: {
      blueWave: 'Blue Wave',
    },
  },

  publicCoral: {
    hero: {
      subtitle: () => `Back the artists you love, and build music's future beyond algorithms.`,
    },
    
    whyBackHere: {
      title: 'What your coral does',
      benefits: {
        first: 'Signals that music matters – builds trust that when artists invest in their art, community shows up',
        second: '$1 here equals 1,000+ streams – real money for artists, not shareholders',
        third: 'Unlocks the Coral Commons – discovery, conversation, and community powered by shared values, not algorithms',
      },
      platformComparison: {
        text: 'More of your money reaches artists. More than',
        linkText: 'anywhere else',
        tooltipHtml: 'Coral: 82 to 88% reaches your artists<br />Patreon: roughly 80 to 87%, plus content obligations<br />Bandcamp: roughly 80% digital, 85% above $5,000<br />Merch: 40 to 60% to artist<br />Streaming: a fraction of a cent per play',
      },
    },
    
    howItWorks: {
      sectionTitle: 'HOW IT WORKS',
      mainTitle: 'Personalise your Coral to back',
      anyArtistsLink: 'any artists',
      anyArtistsTooltip: "Nominate artists, even if they're not on our platform.<br />We'll notify them of waiting funds.<br /><br />This community-driven approach reflects recognition of <br />the value we've already received from their music, and<br />is how we can step forward to value them.<br /><br />Funds wait until claimed. You can refund uncollected funds anytime if you change your mind.",
      subtitle: 'you value with regular micro-signals of appreciation for their art.',
      directContribution: 'Your monthly contribution goes directly to your chosen artists, so they can make more of the music you love.',
      enjoyment: 'Enjoy vibrant, diverse music scenes you help sustain.',
    },
    
    vision: {
      sectionTitle: 'OUR VISION',
      description: 'A thriving ecosystem where music is valued and diverse styles and scenes can prosper.',
    },
    
    coralDynamics: {
      sectionTitle: 'CORAL DYNAMICS',
    },
    
    support: {
      sectionTitle: 'SUPPORT',
      faqTitle: 'FAQs',
      faqDescription: 'Everything you need to know about backing artists through wearecoral.',
    },
    
    cta: {
      buildCoral: 'Build Your Coral',
      startCoral: 'Start Your Coral',
    },
    
    altText: {
      blueWave: 'Blue Wave',
      darkBlueWave: 'Dark Blue Wave',
    },
    
    meta: {
      title: (coralName: string) => `Back ${coralName} | on Coral`,
      description: 'Back the music that moves you. Join a music ecosystem.',
    },
  },
  
  collectiveImpactCalculator: {
    contributionLabel: 'Your monthly contribution',
    perArtistLine: (artistCount: number, perArtist: string) =>
      `If you add ${artistCount} artists to your coral that's $${perArtist} per artist.`,
    participantsLine: (participants: string) =>
      `If only ${participants} people contribute like you, that's...`,
    perArtistEachMonth: 'per artist, each month',
    scaleNote: 'Notice how quickly our impact scales even with small amounts.',
  },

  navigation: {
    // Will be populated when we expand beyond Home page
  },
  
  footer: {
    // Will be populated when we expand beyond Home page
  },
};